import React, { useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";

function errorMessage(error) {
  switch (error.code) {
    case "auth/invalid-credential":
      return "The email or password is incorrect.";
    case "auth/email-already-in-use":
      return "An account already uses this email. Please sign in.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Choose a password with at least 6 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Please check your internet connection and try again.";
    default:
      return "We could not complete your request. Please try again.";
  }
}

export default function ParentLogin() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const nav = useNavigate();
  const location = useLocation();
  const registering = mode === "register";

  function goToPortal() {
    const from = location.state?.from;
    const destination =
      from?.pathname?.startsWith("/") &&
      !from.pathname.startsWith("//") &&
      from.pathname !== "/parent-login"
        ? {
            pathname: from.pathname,
            search: from.search || "",
            hash: from.hash || "",
          }
        : "/parent";

    nav(destination, { replace: true });
  }
  async function resetPassword() {
  if (busy) return;

  const resetEmail = email.trim();

  if (!resetEmail) {
    setError("Enter your email address first, then click Forgot password.");
    return;
  }

  setError("");
  setBusy(true);

  try {
    await sendPasswordResetEmail(auth, resetEmail);
    window.alert(
      "If an account exists for this email, you’ll receive a password reset link. Please check your inbox and spam folder."
    );
  } catch (resetError) {
    setError(errorMessage(resetError));
  } finally {
    setBusy(false);
  }
}
  async function submit(event) {
    event.preventDefault();
    if (busy) return;

    setError("");

    if (registering && !name.trim()) {
      setError("Please enter the parent or guardian's name.");
      return;
    }

    setBusy(true);

    try {
      if (!registering) {
        await signInWithEmailAndPassword(auth, email.trim(), password);
        goToPortal();
        return;
      }

      let account = pendingUser;

      if (!account) {
        const credential = await createUserWithEmailAndPassword(
          auth,
          email.trim(),
          password
        );

        account = credential.user;
        setPendingUser(account);
        setPassword("");
      }

      if (auth.currentUser?.uid !== account.uid) {
        setError(
          "Your sign-in changed. Please refresh the page and sign in again."
        );
        return;
      }

      try {
        await setDoc(doc(db, "users", account.uid), {
          name: name.trim(),
          email: account.email,
          role: "parent",
          createdAt: serverTimestamp(),
        });
      } catch (profileError) {
        console.error("Unable to save parent profile:", profileError);
        setError(
          "Your login account was created, but your profile could not be saved. Click Retry Saving Profile. If this continues, contact the center."
        );
        return;
      }

      goToPortal();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  const inputClass = "w-full rounded-xl border p-3";

  return (
    <div className="mx-auto max-w-md rounded-3xl bg-white p-7 shadow-sm ring-1 ring-slate-200">
      <p className="text-sm font-semibold text-sky-700">Family access</p>

      <h1 className="mt-2 text-3xl font-bold text-slate-900">
        {registering ? "Create Parent Account" : "Parent Login"}
      </h1>

      <p className="mt-2 text-sm text-slate-600">
        Secure access for enrolled and prospective families.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <fieldset disabled={busy} className="space-y-4">
          {registering && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                Parent/guardian name
              </span>
              <input
                className={inputClass}
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </label>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium">Email</span>
            <input
              className={inputClass}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={Boolean(pendingUser)}
              required
            />
          </label>

          {!pendingUser && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                Password
              </span>
              <input
                className={inputClass}
                type="password"
                autoComplete={
                  registering ? "new-password" : "current-password"
                }
                minLength={registering ? 6 : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="w-full rounded-xl bg-sky-700 px-5 py-3 font-semibold text-white disabled:opacity-60"
          >
            {busy
              ? "Please wait…"
              : pendingUser
                ? "Retry Saving Profile"
                : registering
                  ? "Create Account"
                  : "Sign In"}
          </button>
        </fieldset>
      </form>

            {!registering && !pendingUser && (
        <button
          type="button"
          onClick={resetPassword}
          disabled={busy}
          className="mt-4 block text-sm font-semibold text-sky-700 disabled:opacity-60"
        >
          Forgot password?
        </button>
      )}

      {!pendingUser && (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setMode(registering ? "login" : "register");
            setPassword("");
            setError("");
          }}
          className="mt-4 block text-sm font-semibold text-sky-700 disabled:opacity-60"
        >
          {registering
            ? "Already have an account? Sign in"
            : "New parent? Create an account"}
        </button>
      )}
    </div>
  );
}
